import { z } from 'zod/v3'

export const RequestContextStatusSchema = z.enum([
  'READY',
  'REQUIRES_HUMAN',
  'BLOCKED',
])
export type RequestContextStatus = z.infer<typeof RequestContextStatusSchema>

export const RuntimePolicyCitationSchema = z.object({
  policyClaimId: z.string(),
  claimFingerprint: z.string(),
  docBlockId: z.string().optional(),
  claimText: z.string(),
  quoteText: z.string().optional(),
})
export type RuntimePolicyCitationDTO = z.infer<
  typeof RuntimePolicyCitationSchema
>

export const RuntimePolicyBasisPolicySchema = z.object({
  policyDocumentId: z.string(),
  policySlug: z.string().optional(),
  policyDocumentVersionId: z.string(),
  policyDocumentVersion: z.number().int().nullable(),
  contentHash: z.string(),
  title: z.string(),
  relevance: z.number().min(0).max(1),
  reason: z.string(),
  citations: z.array(RuntimePolicyCitationSchema),
})
export type RuntimePolicyBasisPolicyDTO = z.infer<
  typeof RuntimePolicyBasisPolicySchema
>

export const RuntimePolicyBasisSchema = z.object({
  schemaVersion: z.literal(1),
  resolvedAt: z.string(),
  queryHash: z.string(),
  method: z.literal('hybrid_agent'),
  modelId: z.string(),
  procedureAlignment: z.literal('consistent'),
  confidence: z.number().min(0).max(1),
  rationale: z.string(),
  candidatePolicyIds: z.array(z.string()),
  policies: z.array(RuntimePolicyBasisPolicySchema),
})
export type RuntimePolicyBasisDTO = z.infer<typeof RuntimePolicyBasisSchema>

export const RequestContextResolutionDTOSchema = z.object({
  schemaVersion: z.literal(1),
  status: RequestContextStatusSchema,
  resolvedAt: z.string(),
  taskId: z.string().nullable(),
  workflowId: z.string(),
  subject: z
    .object({
      employeeId: z.string(),
      userId: z.string().nullable(),
      email: z.string(),
      employmentStatus: z.string(),
      lifecycleStatus: z.string(),
      startDate: z.string().nullable(),
      department: z.string().nullable(),
      title: z.string().nullable(),
      managerEmployeeId: z.string().nullable(),
      roleDefinitionId: z.string().nullable(),
      personGroupIds: z.array(z.string()),
    })
    .nullable(),
  externalPrincipals: z.array(
    z.object({
      provider: z.string(),
      connectionId: z.string().nullable(),
      externalId: z.string(),
      email: z.string().nullable(),
      status: z.string(),
      isPrivileged: z.boolean().nullable(),
      isMfaEnrolled: z.boolean().nullable(),
      observedAt: z.string(),
      canonicalSchemaVersion: z.number().int(),
      canonicalLossy: z.boolean(),
    }),
  ),
  groupMemberships: z.array(
    z.object({
      provider: z.string(),
      groupExternalId: z.string(),
      groupName: z.string(),
      memberExternalId: z.string(),
      observedAt: z.string(),
    }),
  ),
  devices: z.array(
    z.object({
      employeeDeviceId: z.string(),
      ownership: z.string().nullable(),
      complianceStatus: z.string(),
      mdmProvider: z.string().nullable(),
      mdmDeviceId: z.string().nullable(),
      lastCheckIn: z.string().nullable(),
    }),
  ),
  target: z.record(z.unknown()),
  policyBasis: RuntimePolicyBasisSchema.nullable(),
  missingFacts: z.array(z.string()),
  ambiguities: z.array(z.string()),
  warnings: z.array(z.string()),
  automationEligibility: z.object({
    eligible: z.boolean(),
    reasonCodes: z.array(z.string()),
  }),
})
export type RequestContextResolutionDTO = z.infer<
  typeof RequestContextResolutionDTOSchema
>

export const COMPLIANCE_OPERATION_KEYS = [
  'identity.account.ensure',
  'identity.group_membership.ensure',
  'identity.account.suspend',
] as const
export const ComplianceOperationKeySchema = z.enum(COMPLIANCE_OPERATION_KEYS)
export type ComplianceOperationKey = z.infer<
  typeof ComplianceOperationKeySchema
>

export const ComplianceOperationRequestSchema = z.object({
  operationKey: ComplianceOperationKeySchema,
  fulfillmentMode: z.literal('human'),
  provider: z.string(),
  connectionId: z.string().nullable(),
  subjectEmployeeId: z.string(),
  subjectExternalId: z.string().optional(),
  targetExternalId: z.string().optional(),
  desiredState: z.record(z.unknown()),
  requestContextHash: z.string(),
  reason: z.string(),
})
export type ComplianceOperationRequest = z.infer<
  typeof ComplianceOperationRequestSchema
>

export const FULFILLMENT_VERIFICATION_RESULTS = [
  'human_fulfilled_provider_verified',
  'human_fulfilled_artifact_verified',
  'already_compliant_provider_verified',
  'manual_pending',
  'denied_by_guard',
  'verification_failed',
  'unresolved',
] as const
export const FulfillmentVerificationResultSchema = z.enum(
  FULFILLMENT_VERIFICATION_RESULTS,
)
export type FulfillmentVerificationResult = z.infer<
  typeof FulfillmentVerificationResultSchema
>

export const ComplianceOperationCompletionSchema = z.object({
  operationKey: ComplianceOperationKeySchema,
  provider: z.string(),
  connectionId: z.string().nullable(),
  subjectExternalId: z.string().nullable(),
  targetExternalId: z.string().nullable(),
  disposition: FulfillmentVerificationResultSchema,
  completedAt: z.string(),
  completedByUserId: z.string(),
  verifiedAt: z.string().nullable(),
  verifiedByUserId: z.string().nullable(),
  artifactIds: z.array(z.string()),
  reference: z.string().nullable(),
  requestContextHash: z.string(),
  observationIds: z.array(z.string()),
  message: z.string().nullable(),
})
export type ComplianceOperationCompletionDTO = z.infer<
  typeof ComplianceOperationCompletionSchema
>

export const DecisionExecutionDTOSchema = z.object({
  executionId: z.string(),
  workflowId: z.string().nullable(),
  workflowName: z.string().nullable(),
  definitionVersion: z.number().int().nullable(),
  status: z.string(),
  triggerCause: z.unknown().nullable(),
  requestContext: RequestContextResolutionDTOSchema.nullable(),
  requestContextHash: z.string().nullable(),
  policyBasis: RuntimePolicyBasisSchema.nullable(),
  approvals: z.array(z.record(z.unknown())),
  fulfillment: z.array(z.record(z.unknown())),
  validation: z.unknown().nullable(),
  outcome: z.string().nullable(),
})
export type DecisionExecutionDTO = z.infer<typeof DecisionExecutionDTOSchema>
